import React, { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useProjectUIStore } from '../store/projectUIStore';
import { useTopicUIStore } from '../store/topicUIStore';
import { Sidebar } from '../components/sidebar/Sidebar';
import { TopicContainer } from '../components/topics/TopicContainer';
import Dashboard from '../components/dashboard/Dashboard';
import { SubscriptionLimitBanner } from '../components/shared/SubscriptionLimitBanner';
import { ProjectAccessCheck } from '../components/router/ProjectAccessCheck';
import { ErrorBoundary } from '../components/ErrorBoundary';

export function MainRoute() {
  const { projectId, topicId } = useParams();
  const { setActiveProject } = useProjectUIStore();
  const { setActiveTopicId } = useTopicUIStore();
  const navigate = useNavigate();
  const location = useLocation();
  const isDashboard = location.pathname === '/dashboard';
  const prevProjectRef = React.useRef<string | null>(null);

  // Handle project and topic selection
  useEffect(() => {
    if (!projectId || isDashboard) {
      if (isDashboard) {
        // Clear active topic when going to dashboard
        setActiveTopicId(null);
      }
      if (!projectId) {
        setActiveProject(null);
        setActiveTopicId(null);
      }
      return;
    }

    // Track if the project ID has changed
    const projectChanged = prevProjectRef.current !== projectId;

    if (projectChanged) {
      // Update tracking ref
      prevProjectRef.current = projectId;

      // Set the active project
      setActiveProject(projectId);
    }
  }, [projectId, isDashboard, setActiveProject, setActiveTopicId]);

  // Handle topic selection
  useEffect(() => {
    if (topicId) {
      setActiveTopicId(topicId);
    }
  }, [topicId, setActiveTopicId]);

  // Extract shared sidebar component with consistent props
  const renderSidebar = () => <Sidebar onProjectSelect={id => navigate(`/project/${id}`)} />;

  // Create the main content wrapper
  const renderContentWrapper = (content: React.ReactNode) => (
    <div
      className="flex h-screen overflow-hidden max-w-[100vw] flex-col"
      data-testid="main-route-container"
    >
      {/* Subscription warning banner */}
      <SubscriptionLimitBanner />

      <div className="flex flex-1 overflow-hidden">
        {renderSidebar()}
        <div className="flex-1 h-full lg:p-3 lg:pl-0">
          <div className="h-full lg:rounded-[10px] lg:shadow-[2px_4px_12px_rgba(0,0,0,0.12)] lg:overflow-hidden lg:bg-[var(--content-bg-color)]">
            {content}
          </div>
        </div>
      </div>
    </div>
  );

  // We'll let the Dashboard and TopicContainer components handle loading completion

  // For dashboard or project view, render the appropriate content
  return (
    <ErrorBoundary
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
          Error loading content
        </div>
      }
    >
      {isDashboard
        ? // Direct rendering for dashboard without project access check
          renderContentWrapper(<Dashboard />)
        : // Project access check only for project routes - but don't remount the entire layout
          renderContentWrapper(
            <ProjectAccessCheck>
              <TopicContainer
                onTopicSelect={id =>
                  navigate(projectId ? `/project/${projectId}/topic/${id}` : `/topic/${id}`)
                }
              />
            </ProjectAccessCheck>
          )}
    </ErrorBoundary>
  );
}
